import { Input } from '@components/ui/input';
import { ChevronDown, ChevronUp, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import {
  escapeRegex,
  type SearchedKeyWord,
  searchForKeyword
} from './searchForKeyword';

export type LogViewerSearchInputProps = {
  lines: string[];
  onExpand: () => void;
  onScrollToRow: (row: number) => void;
  onSearchChange: (searchTerm: string) => void;
};

/**
 * Standalone search input placed in the accordion header (always visible).
 * Manages its own search state against the provided log lines.
 *
 * Notes:
 * - The accordion is opened the first time the user types something
 *   non-empty, then never re-opened by this component (the user may have
 *   collapsed it intentionally).
 * - The user's selected match position is preserved across `lines`
 *   replacements (live polling); we only reset it when the search term
 *   changes.
 */
export const LogViewerSearchInput = ({
  lines,
  onExpand,
  onScrollToRow,
  onSearchChange
}: LogViewerSearchInputProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const onExpandRef = useRef(onExpand);
  const onScrollToRowRef = useRef(onScrollToRow);
  const onSearchChangeRef = useRef(onSearchChange);
  const hasExpandedRef = useRef(false);

  // Track callbacks via refs so the search effect doesn't need to depend on
  // their (typically unstable) identities — callers regularly pass inline
  // arrow functions.
  useEffect(() => {
    onExpandRef.current = onExpand;
    onScrollToRowRef.current = onScrollToRow;
    onSearchChangeRef.current = onSearchChange;
  });

  const [localValue, setLocalValue] = useState('');
  const [results, setResults] = useState<SearchedKeyWord[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  const hasFoundResults = results.length > 0 && results[0]?.rowIndex !== -1;
  const totalResults = hasFoundResults ? results.length : 0;
  const displayIndex = hasFoundResults ? currentIndex + 1 : 0;

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Re-run search when the term changes. Lines updating (via polling) are
  // intentionally NOT a dependency: if the user is mid-search we want to
  // keep their position. They can manually re-search if they want fresh
  // matches against new lines.
  useEffect(() => {
    const adjustedInput = escapeRegex(localValue);

    if (adjustedInput !== '') {
      if (!hasExpandedRef.current) {
        onExpandRef.current();
        hasExpandedRef.current = true;
      }
      const foundIndexes = searchForKeyword(adjustedInput, lines, lines.length);

      setResults(foundIndexes);
      if (foundIndexes.length !== 0 && foundIndexes[0].rowIndex !== -1) {
        setCurrentIndex(0);
        onScrollToRowRef.current(foundIndexes[0].rowIndex);
      }
    } else {
      setResults([]);
      setCurrentIndex(0);
    }

    onSearchChangeRef.current(adjustedInput);
    // We intentionally exclude `lines` from deps; see comment above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localValue, lines]);

  const handleNext = () => {
    if (!hasFoundResults) {
      return;
    }
    const next = (currentIndex + 1) % results.length;
    setCurrentIndex(next);
    onScrollToRow(results[next].rowIndex);
  };

  const handlePrev = () => {
    if (!hasFoundResults) {
      return;
    }
    let prev = currentIndex - 1;
    if (prev < 0) {
      prev += results.length;
    }
    setCurrentIndex(prev);
    onScrollToRow(results[prev].rowIndex);
  };

  const handleClear = () => {
    setLocalValue('');
    setResults([]);
    setCurrentIndex(0);
    onSearchChange('');
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (e.shiftKey) {
        handlePrev();
      } else {
        handleNext();
      }
      return;
    }
    if (e.key === 'Escape' && localValue !== '') {
      e.preventDefault();
      handleClear();
    }
  };

  return (
    <div
      className="mr-4 flex items-center gap-1.5"
      onClick={e => e.stopPropagation()}
    >
      <div className="relative">
        <Input
          className="h-7 w-56 pr-16 text-xs no-underline"
          onChange={e => setLocalValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Search logs..."
          ref={inputRef}
          value={localValue}
        />
        {localValue && (
          <div className="-translate-y-1/2 absolute top-1/2 right-2 flex items-center gap-1">
            <span className="text-muted-foreground text-xs tabular-nums">
              {displayIndex}/{totalResults}
            </span>
            <button
              aria-label="Clear search"
              className="text-muted-foreground hover:text-foreground"
              onClick={handleClear}
              type="button"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        )}
      </div>
      <button
        aria-label="Previous match"
        className="flex h-7 w-7 items-center justify-center rounded-md border border-input text-muted-foreground hover:bg-accent hover:text-foreground disabled:pointer-events-none disabled:opacity-50"
        disabled={!hasFoundResults}
        onClick={handlePrev}
        title="Previous match (Shift+Enter)"
        type="button"
      >
        <ChevronUp className="h-3.5 w-3.5" />
      </button>
      <button
        aria-label="Next match"
        className="flex h-7 w-7 items-center justify-center rounded-md border border-input text-muted-foreground hover:bg-accent hover:text-foreground disabled:pointer-events-none disabled:opacity-50"
        disabled={!hasFoundResults}
        onClick={handleNext}
        title="Next match (Enter)"
        type="button"
      >
        <ChevronDown className="h-3.5 w-3.5" />
      </button>
    </div>
  );
};

export { LogViewerSearchSync } from './LogViewerSearchSync';
// Re-exports preserve backward compatibility for existing imports.
export { searchForKeyword } from './searchForKeyword';
